import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FlaskConical, FileText, ExternalLink, Folder, Trash2, ArrowLeft, Search, Loader2, FileSearch, Undo2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AdminBriteLabsResearchTab() {
  const [labs, setLabs] = useState([]);
  const [labSearch, setLabSearch] = useState('');
  const [selectedLab, setSelectedLab] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDeleted, setShowDeleted] = useState(true);
  // Map of lab_id -> {active, deleted} so the lab list can highlight labs
  // that currently have deleted files awaiting admin review.
  const [labCounts, setLabCounts] = useState({});

  const loadLabs = useCallback(() => {
    setLoadingLabs(true);
    fetch(`${BACKEND_URL}/api/admin/britelabs`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(async (ls) => {
        setLabs(ls);
        // Fan-out fetch per-lab summary in parallel (cheap, projection-only).
        const counts = {};
        await Promise.all((ls || []).map(async (lab) => {
          try {
            const r = await fetch(`${BACKEND_URL}/api/admin/britelabs/${lab.lab_id}/research?include_deleted=true`, { credentials: 'include' });
            if (!r.ok) return;
            const d = await r.json();
            const its = d.items || [];
            counts[lab.lab_id] = {
              active: its.filter(i => !i.deleted).length,
              deleted: its.filter(i => i.deleted).length,
            };
          } catch { /* ignore */ }
        }));
        setLabCounts(counts);
      })
      .catch(() => toast.error('Failed to load BriteLabs'))
      .finally(() => setLoadingLabs(false));
  }, []);

  useEffect(() => { loadLabs(); }, [loadLabs]);

  // Refresh lab list whenever the browser tab becomes visible again so a
  // delete that happened on the BriteLab detail page shows up here without
  // a manual reload.
  useEffect(() => {
    const onFocus = () => loadLabs();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadLabs]);

  const loadResearch = (lab) => {
    setSelectedLab(lab);
    setLoadingItems(true);
    fetch(`${BACKEND_URL}/api/admin/britelabs/${lab.lab_id}/research?include_deleted=true`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setItems(d.items || []))
      .catch(() => toast.error('Failed to load research items'))
      .finally(() => setLoadingItems(false));
  };

  const refresh = () => {
    if (!selectedLab) return;
    setLoadingItems(true);
    fetch(`${BACKEND_URL}/api/admin/britelabs/${selectedLab.lab_id}/research?include_deleted=true`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setItems(d.items || []))
      .catch(() => {})
      .finally(() => setLoadingItems(false));
  };

  const handleDelete = async (item) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/britelabs/${selectedLab.lab_id}/research/${item.message_id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Deleted "${d.filename}". You can restore it from this view.`);
      setDeleteConfirm(null);
      refresh();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const handleRestore = async (item) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/britelabs/${selectedLab.lab_id}/research/${item.message_id}/restore`,
        { method: 'POST', credentials: 'include' },
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Restored "${d.filename}"`);
      refresh();
    } catch (e) { toast.error(e.message || 'Restore failed'); }
  };

  const filteredLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();
    return labs.filter(l => !q || (l.title || '').toLowerCase().includes(q));
  }, [labs, labSearch]);

  const visibleItems = useMemo(() => {
    if (showDeleted) return items;
    return items.filter(i => !i.deleted);
  }, [items, showDeleted]);

  const activeCount = items.filter(i => !i.deleted).length;
  const deletedCount = items.filter(i => i.deleted).length;

  // ----- Lab selection (default view) -----
  if (!selectedLab) {
    return (
      <div className="space-y-4" data-testid="admin-britelab-research-tab">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-brand-navy" /> Research Management
            </h3>
            <p className="text-sm text-slate-500">Select a BriteLab to review or restore its attached research files.</p>
          </div>
          <Button size="sm" variant="outline" onClick={loadLabs} disabled={loadingLabs}
            data-testid="research-labs-refresh"
            className="h-8 text-xs flex-shrink-0">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loadingLabs ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="research-lab-search" placeholder="Search BriteLabs..."
            value={labSearch} onChange={e => setLabSearch(e.target.value)} className="pl-8 h-9" />
        </div>

        {loadingLabs ? (
          <Card className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
          </Card>
        ) : filteredLabs.length === 0 ? (
          <Card className="p-8 text-center">
            <FlaskConical className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No BriteLabs found.</p>
          </Card>
        ) : (
          <Card className="divide-y" data-testid="research-lab-list">
            {filteredLabs.map(l => {
              const cnt = labCounts[l.lab_id] || { active: 0, deleted: 0 };
              return (
                <button key={l.lab_id} type="button" onClick={() => loadResearch(l)}
                  data-testid={`research-pick-lab-${l.lab_id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left">
                  <FlaskConical className="h-4 w-4 text-brand-navy flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{l.title}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                      <span>{l.category || 'Uncategorised'}</span>
                      <span>·</span>
                      <span>{(l.invited_users || []).length} member{(l.invited_users || []).length === 1 ? '' : 's'}</span>
                      <span>·</span>
                      <span>{l.status || 'pending'}</span>
                      <span>·</span>
                      <span>{cnt.active} file{cnt.active === 1 ? '' : 's'}</span>
                      {cnt.deleted > 0 && (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] uppercase tracking-wide font-semibold ml-1"
                          data-testid={`lab-deleted-count-${l.lab_id}`}>
                          {cnt.deleted} deleted
                        </Badge>
                      )}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">Review &rarr;</span>
                </button>
              );
            })}
          </Card>
        )}
      </div>
    );
  }

  // ----- Research items view -----
  return (
    <div className="space-y-4" data-testid="admin-britelab-research-detail">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button type="button" onClick={() => { setSelectedLab(null); setItems([]); }}
            data-testid="research-back-btn"
            className="text-xs text-brand-navy hover:underline flex items-center gap-1 mb-1">
            <ArrowLeft className="h-3 w-3" /> All BriteLabs
          </button>
          <h3 className="text-lg font-semibold truncate">{selectedLab.title}</h3>
          <p className="text-sm text-slate-500">
            {activeCount} active{deletedCount > 0 ? <> · <span className="text-rose-600 font-medium">{deletedCount} deleted</span></> : null}
          </p>
        </div>
        {deletedCount > 0 && (
          <Button size="sm" variant="outline" onClick={() => setShowDeleted(s => !s)}
            data-testid="toggle-show-deleted"
            className="flex-shrink-0 h-8 text-xs">
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={refresh} disabled={loadingItems}
          data-testid="research-items-refresh"
          className="flex-shrink-0 h-8 text-xs">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loadingItems ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loadingItems ? (
        <Card className="p-8 text-center text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading research…
        </Card>
      ) : visibleItems.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No research has been attached to this BriteLab.</p>
        </Card>
      ) : (
        <Card className="divide-y" data-testid="research-item-list">
          {visibleItems.map(it => {
            const isFile = !!it.doc_id;
            const url = it.research_url;
            const isDeleted = !!it.deleted;
            return (
              <div key={it.message_id} data-testid={`research-row-${it.message_id}`}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 hover:bg-slate-50 ${isDeleted ? 'bg-rose-50/40' : ''}`}>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {isFile ? <FileText className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDeleted ? 'text-rose-400' : 'text-brand-navy'}`} />
                          : <ExternalLink className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDeleted ? 'text-rose-400' : 'text-brand-sage'}`} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-medium truncate ${isDeleted ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                        {it.filename || it.content}
                      </p>
                      {isDeleted && (
                        <Badge variant="outline" data-testid={`deleted-pill-${it.message_id}`}
                          className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] uppercase tracking-wide font-semibold">
                          Deleted
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      <Folder className="h-3 w-3 inline mr-1 text-slate-400" />
                      {it.folder_name || 'Uncategorised'}
                      {' · '}
                      Shared by {it.sender_name}
                      {it.file_size ? ` · ${formatBytes(it.file_size)}` : ''}
                      {' · '}
                      {it.timestamp ? new Date(it.timestamp).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {url && !isDeleted && (
                    <Button asChild size="sm" variant="outline" className="h-8 text-xs">
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        data-testid={`research-view-${it.message_id}`}>
                        View
                      </a>
                    </Button>
                  )}
                  {isDeleted ? (
                    <Button size="sm" variant="outline" onClick={() => handleRestore(it)}
                      data-testid={`research-restore-${it.message_id}`}
                      className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                      <Undo2 className="h-3.5 w-3.5 mr-1" /> Restore
                    </Button>
                  ) : deleteConfirm === it.message_id ? (
                    <div className="flex gap-1.5 items-center">
                      <span className="text-xs text-red-600 font-medium">Delete?</span>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(it)}
                        data-testid={`research-confirm-delete-${it.message_id}`}
                        className="h-7 px-2 text-xs">Yes</Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)}
                        data-testid={`research-cancel-delete-${it.message_id}`}
                        className="h-7 px-2 text-xs">No</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(it.message_id)}
                      data-testid={`research-delete-${it.message_id}`}
                      className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
